import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Dialog,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import GoalngoLogo from "../../../../../assets/images/payments/goalngo-invoice-icon.png";
import { PaymentDownloadInvoiceIcon } from "../../../../../assets/icons/MUI/PaymentIcons";
import { useInvoiceDetails } from "../../../../../api/queries/payment/paymentQueries";

import { InvoiceBodyCellProps, InvoicePopUpProps } from "../../../payment.types";

const invoiceHeadingStyle = {
  color: "var(--Gray-600, #5E6470)",
  fontFamily: "Montserrat",
  fontSize: "10px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "14px",
};

const invoiceBodyTitleStyle = {
  color: "var(--Gray-600, #5E6470)",
  fontFamily: "Montserrat",
  fontSize: "10px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "14px",
};

const invoiceBodyDataStyle = {
  color: "var(--Gray-900, #1A1C21)",
  fontFamily: "Montserrat",
  fontSize: "10px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "14px",
};

const invoiceTableHeaderCellStyle = {
  border: "none",
  padding: "8px 0",
  color: "var(--Gray-600, #5E6470)",
  fontFamily: "Montserrat",
  fontSize: "8px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "12px",
  letterSpacing: "0.32px",
  textTransform: "uppercase",
};

const invoiceTableBodyCellStyle = {
  border: "none",
  padding: "12px 0",
  color: "var(--Gray-900, #1A1C21)",
  fontFamily: "Montserrat",
  fontSize: "10px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "14px",
};

export const InvoicePopUp = ({ open, onClose, invoiceId }: InvoicePopUpProps) => {
  const { t } = useTranslation();
  const invoiceRef = useRef<HTMLDivElement>(null);

  const handleDownloadPDF = () => {
    const input = invoiceRef.current;
    if (!input) return;

    html2canvas(input, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save("invoice.pdf");
    });
  };

  const { invoiceDetails: invoice, isInvoiceDetailsLoading, isInvoiceDetailsHasError } = useInvoiceDetails(invoiceId);

  return (
    <Dialog
      onClose={() => onClose()}
      open={open}
      sx={{
        "& .MuiDialog-paper": {
          minHeight: "50vh",
          width: "100%",
          maxWidth: "595px",
          background: "#F9FBFA",
          borderRadius: "0",
        },
      }}
      scroll="body"
    >
      {isInvoiceDetailsLoading || !invoice ? (
        <InvoiceLoader />
      ) : (
        <Box
          component="div"
          ref={invoiceRef}
          sx={{
            padding: "20px 16px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              marginBottom: "24px",
              position: "relative",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "2px" }}>
              <img src={GoalngoLogo} width="94.56px" height="34px" />
              <Typography sx={invoiceHeadingStyle}>{invoice.goalngoWebsite}</Typography>
              <Typography sx={invoiceHeadingStyle}>{invoice.goalngoEmail}</Typography>
              <Typography sx={invoiceHeadingStyle}>{invoice.goalngoNumber}</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", rowGap: "2px", alignItems: "flex-end" }}>
              <IconButton onClick={handleDownloadPDF}>
                <PaymentDownloadInvoiceIcon />
              </IconButton>
              {invoice.entityAddress?.length > 0 && (
                <>
                  <Typography sx={invoiceHeadingStyle}>{t("pages.payment.paymentLinks.businessAddress")}</Typography>
                  <Typography sx={invoiceHeadingStyle}>{invoice.entityAddress}</Typography>
                </>
              )}
              {invoice.taxId?.length > 0 && (
                <Typography sx={invoiceHeadingStyle}>
                  {t("pages.payment.paymentLinks.taxId")} {invoice.taxId}
                </Typography>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              borderRadius: "12px",
              border: "0.5px solid var(--Gray-100, #D7DAE0)",
              background: "var(--White, #FFF)",
              padding: "20px 16px",
              marginBottom: "24px",
              minHeight: "644px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "24px" }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", flex: 1, maxWidth: "105px", rowGap: "24px" }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", flex: 1 }}>
                  <InvoiceBodyCell title={t("pages.payment.paymentLinks.billedTo")} value={invoice.billedToName} />
                  <InvoiceBodyCell title={t("pages.payment.paymentLinks.address")} value={invoice.billedToAddress} />
                  <InvoiceBodyCell
                    title={t("pages.payment.paymentLinks.subject")}
                    value={t(`pages.payment.paymentLinks.${invoice.subject}`)}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  flex: 1,
                  maxWidth: "105px",
                  rowGap: "24px",
                }}
              >
                <InvoiceBodyCell title={t("pages.payment.paymentLinks.invoiceNumber")} value={invoice.invoiceNumber} />
                <InvoiceBodyCell title={t("pages.payment.paymentLinks.reference")} value={invoice.reference} />
                <InvoiceBodyCell title={t("pages.payment.paymentLinks.invoiceDate")} value={invoice.invoiceDate} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  flex: 1,
                  maxWidth: "105px",
                  rowGap: "24px",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", rowGap: "4px", alignItems: "flex-end" }}>
                  <Typography sx={invoiceBodyTitleStyle}>{`${t(
                    "pages.payment.paymentLinks.dueDate"
                  )} (${invoice.currency.toUpperCase()})`}</Typography>
                  <Typography
                    sx={{
                      color: "var(--Green---100, #106048)",
                      fontFamily: "Montserrat",
                      fontSize: "20px",
                      fontStyle: "normal",
                      fontWeight: 700,
                      lineHeight: "28px",
                    }}
                  >
                    {/* {invoice.total / 100} */}
                    {invoice.amountDue / 100}
                  </Typography>
                </Box>

                <InvoiceBodyCell title={t("pages.payment.paymentLinks.dueDate")} value={invoice.dueDate} alignEnd />
              </Box>
            </Box>
            <TableContainer sx={{ flex: 1 }}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      borderTop: "1px solid #D7DAE0",
                      borderBottom: "1px solid #D7DAE0",
                    }}
                  >
                    <TableCell
                      sx={{
                        width: "50%",
                        ...invoiceTableHeaderCellStyle,
                      }}
                    >
                      {t("pages.payment.paymentLinks.description")}
                    </TableCell>
                    <TableCell sx={{ width: "16.66%", textAlign: "right", ...invoiceTableHeaderCellStyle }}>
                      {t("pages.payment.paymentLinks.quantity")}
                    </TableCell>
                    <TableCell sx={{ width: "16.66%", textAlign: "right", ...invoiceTableHeaderCellStyle }}>
                      {t("pages.payment.paymentLinks.unitPrice")}
                    </TableCell>
                    <TableCell sx={{ width: "16.66%", textAlign: "right", ...invoiceTableHeaderCellStyle }}>
                      {t("pages.payment.paymentLinks.amount")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ width: "50%", ...invoiceTableBodyCellStyle }}>{item.description}</TableCell>
                      <TableCell
                        sx={{
                          width: "16.66%",
                          textAlign: "right",
                          ...invoiceTableBodyCellStyle,
                        }}
                      >
                        {item.quantity}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "16.66%",
                          textAlign: "right",
                          ...invoiceTableBodyCellStyle,
                        }}
                      >
                        {item.unitPrice / 100}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "16.66%",
                          textAlign: "right",
                          ...invoiceTableBodyCellStyle,
                        }}
                      >
                        {item.total / 100}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={4} sx={{ borderBottom: "1px solid #D7DAE0", padding: 0 }} />
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: "right", ...invoiceTableBodyCellStyle }}>
                      {t("pages.payment.paymentLinks.subtotal")}
                    </TableCell>
                    <TableCell sx={{ textAlign: "right", ...invoiceTableBodyCellStyle }}>
                      {invoice.subtotal / 100}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: "right", ...invoiceTableBodyCellStyle }}>
                      {t("pages.payment.paymentLinks.total")}
                    </TableCell>
                    <TableCell sx={{ textAlign: "right", ...invoiceTableBodyCellStyle }}>
                      {invoice.total / 100}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{
                        textAlign: "right",
                        border: "none",
                        padding: "12px 0",
                        color: "var(--Gray-900, #1A1C21)",
                        fontFamily: "Montserrat",
                        fontSize: "10px",
                        fontStyle: "normal",
                        fontWeight: 700,
                        lineHeight: "14px",
                      }}
                    >
                      {t("pages.payment.paymentLinks.amountDue")}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                        border: "none",
                        padding: "12px 0",
                        color: "var(--Gray-900, #1A1C21)",
                        fontFamily: "Montserrat",
                        fontSize: "10px",
                        fontStyle: "normal",
                        fontWeight: 700,
                        lineHeight: "14px",
                      }}
                    >
                      {invoice.amountDue / 100}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ border: "none", padding: 0 }} />
                    <TableCell colSpan={2} sx={{ borderBottom: "1px solid #D7DAE0", padding: 0 }} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              sx={{
                color: "var(--Gray-900, #1A1C21)",
                fontFamily: "Montserrat",
                fontSize: "10px",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "14px",
                alignSelf: "flex-end",
              }}
            >
              {t("pages.payment.paymentLinks.thankYou")}
            </Typography>
          </Box>
          <Typography
            sx={{
              color: "var(--Gray-600, #5E6470)",
              fontFamily: "Montserrat",
              fontSize: "10px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "14px",
            }}
          >
            {t("pages.payment.paymentLinks.termsConditions")}
          </Typography>
          <Typography
            sx={{
              color: "var(--Gray-900, #1A1C21)",
              fontFamily: "Montserrat",
              fontSize: "10px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "14px",
            }}
          >
            {t("pages.payment.paymentLinks.paymentNote")}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

const InvoiceBodyCell = ({ title, value, alignEnd }: InvoiceBodyCellProps) => {
  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", rowGap: "4px", alignItems: alignEnd ? "flex-end" : "flex-start" }}
    >
      <Typography sx={invoiceBodyTitleStyle}>{title}</Typography>
      <Typography sx={invoiceBodyDataStyle}>{value}</Typography>
    </Box>
  );
};

export const InvoiceLoader = () => {
  return (
    <Box
      component="div"
      sx={{
        padding: "20px 16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: "24px",
          position: "relative",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: "2px" }}>
          <Skeleton variant="rectangular" width={100} height={25} />
          <Skeleton variant="text" width={120} />
          <Skeleton variant="text" width={140} />
          <Skeleton variant="text" width={100} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: "2px", alignItems: "flex-end" }}>
          <Skeleton variant="text" width={150} />
          <Skeleton variant="text" width={130} />
          <Skeleton variant="text" width={90} />
        </Box>
      </Box>

      {/* Invoice Content Skeleton */}
      <Box
        sx={{
          borderRadius: "12px",
          border: "0.5px solid var(--Gray-100, #D7DAE0)",
          background: "var(--White, #FFF)",
          padding: "20px 16px",
          marginBottom: "24px",
          minHeight: "644px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "24px" }}>
          {[...Array(3)].map((_, index) => (
            <Box key={index} sx={{ display: "flex", flexDirection: "column", maxWidth: "105px", rowGap: "24px" }}>
              <Skeleton variant="text" width={100} />
              <Skeleton variant="text" width={100} />
              <Skeleton variant="text" width={100} />
            </Box>
          ))}
        </Box>

        {/* Table Skeleton */}
        <TableContainer sx={{ flex: 1 }}>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  borderTop: "1px solid #D7DAE0",
                  borderBottom: "1px solid #D7DAE0",
                }}
              >
                {[...Array(4)].map((_, index) => (
                  <TableCell key={index} sx={{ width: "25%" }}>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(3)].map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {[...Array(4)].map((_, colIndex) => (
                    <TableCell key={colIndex}>
                      <Skeleton variant="text" width="60%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
